import { usePaginatedResource } from '@/shared/hooks/use-paginated-resource'
import clientApplicationRequest from '@fingerprinting/api/client/application'
import type { TClientApplicationDetail } from '@fingerprinting/types/schemas/application.schema'
import type { ApiResponse, PaginatedData } from '@/shared/schemas/api-schema'

const SEARCH_DEBOUNCE_MS = 300
const DEFAULT_PAGE_SIZE = 10

export const STAFF_APPLICATION_LIST_QUERY_KEY = ['staff-applications'] as const

interface StaffApplicationFilters {
  typeCode?: string
  status?: string
  date?: string
  startDate?: string
  endDate?: string
  search?: string
}

/**
 * Custom hook for fetching paginated staff application list with filters
 * Uses the usePaginatedResource hook for data fetching, caching, pagination, and search debouncing
 */
export function useStaffApplicationList(initialFilters?: Partial<StaffApplicationFilters>) {
  return usePaginatedResource<TClientApplicationDetail, StaffApplicationFilters>({
    resourceId: STAFF_APPLICATION_LIST_QUERY_KEY[0],
    fetchFn: async (params): Promise<ApiResponse<PaginatedData<TClientApplicationDetail>>> => {
      const response = await clientApplicationRequest.getListApplications({
        page: params.pageNumber,
        pageSize: params.pageSize,
        typeCode: params.typeCode,
        status: params.status,
        date: params.date,
        startDate: params.startDate,
        endDate: params.endDate,
        search: params.search,
        sortBy: params.sortBy,
        sortDirection: params.sortDirection,
      })
      // Transform the response to match the expected ApiResponse format
      return {
        statusCode: response.statusCode,
        message: response.message,
        data: response.data,
      }
    },
    initialFilters,
    searchField: 'search',
    searchDebounceMs: SEARCH_DEBOUNCE_MS,
    initialPageSize: DEFAULT_PAGE_SIZE,
    refetchOnMount: 'always',
  })
}
